/**
 * customFieldHelpers — Shared utilities for dynamic custom field rendering,
 * search, and export across all system modules.
 *
 * Usage example (list page):
 *   import {
 *     formatCustomFieldValue,
 *     rawCustomFieldValue,
 *     getListFields,
 *     getSearchableFields,
 *     getExportableFields,
 *     customFieldMatchesSearch,
 *   } from '../../utils/customFieldHelpers';
 */
import React from 'react';
import { Chip, Stack, Typography } from '@mui/material';

// ── Field visibility selectors ────────────────────────────────────────────────

/** Returns active fields that should appear as list/table columns. */
export function getListFields(fieldDefs) {
  return (fieldDefs || []).filter(f => f.is_active && f.show_in_list);
}

/** Returns active fields that should appear in create forms. */
export function getCreateFields(fieldDefs) {
  return (fieldDefs || []).filter(f => f.is_active && f.show_in_create);
}

/** Returns active fields that should appear in edit forms. */
export function getEditFields(fieldDefs) {
  return (fieldDefs || []).filter(f => f.is_active && f.show_in_edit);
}

/** Returns active fields that should appear in detail views. */
export function getDetailFields(fieldDefs) {
  return (fieldDefs || []).filter(f => f.is_active && f.show_in_detail);
}

/** Returns active fields included in search. */
export function getSearchableFields(fieldDefs) {
  return (fieldDefs || []).filter(f => f.is_active && f.is_searchable !== false);
}

/** Returns active fields included in CSV/Excel export. */
export function getExportableFields(fieldDefs) {
  return (fieldDefs || []).filter(f => f.is_active && f.is_exportable !== false);
}

// ── Value formatters ──────────────────────────────────────────────────────────

/**
 * Format a custom field value for display in table cells and detail views.
 * Returns JSX (Chip components for boolean/select/multiselect) or a plain string.
 *
 * @param {object} field    - field definition with field_type, options, etc.
 * @param {*}      rawValue - the raw stored value
 * @returns {string|JSX.Element}
 */
export function formatCustomFieldValue(field, rawValue) {
  if (rawValue === null || rawValue === undefined || rawValue === '') return '\u2014';
  switch (field?.field_type) {
    case 'boolean':
    case 'checkbox':
      return rawValue === true || rawValue === 'true'
        ? <Chip label="Po" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.67rem' }} />
        : <Chip label="Jo" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.67rem' }} />;
    case 'date':
      try { return new Date(rawValue + 'T12:00:00').toLocaleDateString('sq-AL'); } catch { return rawValue; }
    case 'currency': {
      const n = Number(rawValue);
      return isNaN(n) ? rawValue : `${n.toLocaleString('de-DE', { minimumFractionDigits: 2 })} \u20ac`;
    }
    case 'number': {
      const n = Number(rawValue);
      return isNaN(n) ? rawValue : n.toLocaleString('de-DE');
    }
    case 'select':
    case 'radio': {
      const opt = (field.options || []).find(o => o.value === rawValue);
      return opt
        ? <Chip label={opt.label} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.67rem', ...(opt.color ? { borderColor: opt.color, color: opt.color } : {}) }} />
        : rawValue;
    }
    case 'multiselect': {
      const vals = Array.isArray(rawValue) ? rawValue : (rawValue || '').split(',').filter(Boolean);
      if (!vals.length) return '\u2014';
      return (
        <Stack direction="row" spacing={0.25} flexWrap="wrap">
          {vals.slice(0, 3).map(v => {
            const opt = (field.options || []).find(o => o.value === v);
            return <Chip key={v} label={opt?.label || v} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.67rem' }} />;
          })}
          {vals.length > 3 && <Chip label={`+${vals.length - 3}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.67rem' }} />}
        </Stack>
      );
    }
    case 'email':
      return <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'primary.main' }} noWrap>{rawValue}</Typography>;
    case 'textarea':
      return String(rawValue).length > 60 ? String(rawValue).slice(0, 60) + '\u2026' : String(rawValue);
    default:
      return String(rawValue).length > 60 ? String(rawValue).slice(0, 60) + '\u2026' : String(rawValue);
  }
}

/**
 * Format a custom field value as a plain string — used for CSV/Excel export.
 *
 * @param {object} field    - field definition
 * @param {*}      rawValue - the raw stored value
 * @returns {string}
 */
export function rawCustomFieldValue(field, rawValue) {
  if (rawValue === null || rawValue === undefined || rawValue === '') return '';
  if (field?.field_type === 'boolean' || field?.field_type === 'checkbox') {
    return rawValue === true || rawValue === 'true' ? 'Po' : 'Jo';
  }
  if (field?.field_type === 'select' || field?.field_type === 'radio') {
    const opt = (field.options || []).find(o => o.value === rawValue);
    return opt ? opt.label : String(rawValue);
  }
  if (field?.field_type === 'multiselect') {
    const vals = Array.isArray(rawValue) ? rawValue : (rawValue || '').split(',').filter(Boolean);
    return vals.map(v => {
      const opt = (field.options || []).find(o => o.value === v);
      return opt ? opt.label : v;
    }).join(', ');
  }
  return String(rawValue);
}

// ── Search helpers ────────────────────────────────────────────────────────────

/**
 * Check whether a record's custom field values contain the search term.
 *
 * @param {Array}         searchableFields - field defs already filtered to is_searchable
 * @param {object}        cfValuesMap      - { [recordId]: { [fieldKey]: value } }
 * @param {string|number} recordId
 * @param {string}        term             - lowercase search term
 * @returns {boolean}
 */
export function customFieldMatchesSearch(searchableFields, cfValuesMap, recordId, term) {
  if (!searchableFields?.length || !term) return false;
  const cfValues = (cfValuesMap || {})[String(recordId)] || {};
  return searchableFields.some(f => {
    const v = cfValues[f.key];
    return v != null && String(v).toLowerCase().includes(term);
  });
}
